/**
 * Wraps one inline artifact card so the transcript's IntersectionObserver can tell when it has scrolled
 * past and its compact copy should pin to the top instead (ACT2-538). Dashboards (via {@link ActivityGroup})
 * and audiences (via `MessageList`) both render through this wrapper, so the DOM contract lives in one place.
 *
 * Only the LATEST card for an identity is observed (`stickySources.get(key) === sourceId`); earlier cards are
 * history and must NOT carry the key attribute, or an in-view edit would unset the pin and hide both. The
 * observed card yields to its pinned copy with `aria-hidden` + `invisible` — invisible, not unmounted, so the
 * box keeps its size and the observer still has geometry to unpin.
 */
import { cn } from '@/_shared/lib/utils';
import { chatArtifactKeyProps } from '@/chat/lib/chat-artifacts';

export interface StickyArtifactSlotProps {
  /** Identity of the artifact this card renders. */
  artifactKey: string;
  /** Position id of THIS card; observed only when it is the latest card for `artifactKey`. */
  sourceId: string;
  /** Identity → position id of the card the pin observes (the latest per identity). */
  stickySources?: ReadonlyMap<string, string>;
  /** Key of the artifact currently pinned to the top — this card yields to that copy. */
  stickyArtifactKey?: string | null;
  className?: string;
  children: React.ReactNode;
}

export function StickyArtifactSlot({
  artifactKey,
  sourceId,
  stickySources,
  stickyArtifactKey = null,
  className,
  children,
}: StickyArtifactSlotProps): React.ReactElement {
  const isStickySource = stickySources?.get(artifactKey) === sourceId;
  const yieldsToPinned = isStickySource && stickyArtifactKey === artifactKey;
  return (
    <div
      {...(isStickySource ? chatArtifactKeyProps(artifactKey) : {})}
      // Omit the attribute entirely rather than emitting `aria-hidden="false"` on every card in the
      // transcript, which React would stringify and any `[aria-hidden]` audit would then trip over.
      {...(yieldsToPinned ? { 'aria-hidden': true } : {})}
      className={cn(className, yieldsToPinned && 'invisible')}
    >
      {children}
    </div>
  );
}
